import { rufe } from './client';

export const UMGEBUNGEN = ['produktiv', 'sandbox'] as const;
export const MAPPINGARTEN = [
  'kundengruppe', 'warengruppe', 'steuersatz', 'zahlungsbedingung', 'kostenstelle',
  /* FIB-25: Erstattung je Zahlungsmittel, Verrechnung unter "ausgleich". */
  'erstattung', 'verrechnung',
] as const;

export type Umgebungskennung = (typeof UMGEBUNGEN)[number];
export type Mappingart = (typeof MAPPINGARTEN)[number];

/**
 * Wohin die Zuordnung in Navision zeigt (FIB-04).
 *
 * Die Namen stehen hier und nicht in der Maske, weil sie aus der Anforderung
 * kommen und nicht aus dem Bildschirmentwurf: Wer die Beschriftung ändert,
 * ändert die Erklärung, nicht die Zuordnung.
 */
export const MAPPINGZIEL: Readonly<Record<Mappingart, string>> = {
  kundengruppe: 'Geschäftsbuchungsgruppe',
  warengruppe: 'Produktbuchungsgruppe',
  steuersatz: 'MwSt-Buchungsgruppe',
  zahlungsbedingung: 'Zahlungsbedingungscode',
  kostenstelle: 'Dimension',
  /* FIB-25: Welches Navision-Feld der Code trifft, ist noch offen (AeA-64). */
  erstattung: 'Buchungscode der Erstattung je Zahlungsweg',
  verrechnung: 'Buchungscode der Verrechnung (Quelle "ausgleich")',
};

/**
 * Eine Navision-Umgebung (FIB-02, FIB-18).
 *
 * `zugangsschluessel` ist ein **Name**, kein Geheimnis — aus ihm bildet der
 * Adapter die Namen seiner Umgebungsvariablen. Kennwörter stehen nie hier;
 * sie wären sonst in jeder Sicherung und in jedem Prüfungsauszug (BP-05).
 */
export type Umgebung = {
  readonly kennung: Umgebungskennung;
  readonly bezeichnung: string;
  readonly soapendpunkt: string | null;
  readonly odataendpunkt: string | null;
  readonly zugangsschluessel: string | null;
  readonly adapter: 'attrappe' | 'soap';
  readonly aktiv: boolean;
  readonly geaendertam: string;
};

export type Mappingzeile = {
  readonly umgebung: Umgebungskennung;
  readonly art: Mappingart;
  readonly quelle: string;
  readonly ziel: string;
  readonly bemerkung: string | null;
  readonly geaendertam: string;
};

export type Standzeile = {
  readonly umgebung: Umgebungskennung;
  readonly belegart: 'rechnung' | 'gutschrift' | 'debitor' | 'artikel' | 'erstattung' | 'verrechnung';
  readonly offen: string;
  readonly laufend: string;
  readonly uebertragen: string;
  readonly fehlerhaft: string;
  readonly aufgegeben: string;
  readonly letzteuebertragung: string | null;
};

export type Uebertragung = {
  readonly id: string;
  readonly belegschluessel: string;
  readonly umgebung: Umgebungskennung;
  readonly belegart: 'rechnung' | 'gutschrift' | 'debitor' | 'artikel' | 'erstattung' | 'verrechnung';
  readonly beleg: string;
  readonly zustand: 'nicht_uebertragen' | 'in_uebertragung' | 'uebertragen' | 'fehlerhaft';
  readonly versuche: number;
  readonly letzterfehler: string | null;
  readonly aufgegebenam: string | null;
  readonly navbelegnummer: string | null;
  readonly uebertragenam: string | null;
};

/**
 * Eine Zeile des Übertragungsprotokolls (FIB-11).
 *
 * OHNE Nutzlast — sie trägt Namen, Anschriften und Beträge, und eine Liste mit
 * hundert Zeilen bräuchte sie hundertmal nicht. Dafür mit dem AUFGELÖSTEN
 * Beleg: `kennzeichen` ist die Rechnungs-, Kunden- oder Artikelnummer,
 * `bezeichnung` der Betreff beziehungsweise Name. Ohne beides stünde dort nur
 * eine Kennung, und niemand erkennt seinen Beleg daran.
 */
export type Protokollzeile = {
  readonly id: string;
  readonly umgebung: Umgebungskennung;
  readonly belegart: 'rechnung' | 'gutschrift' | 'debitor' | 'artikel' | 'erstattung' | 'verrechnung';
  readonly beleg: string;
  readonly belegschluessel: string;
  readonly zustand: 'nicht_uebertragen' | 'in_uebertragung' | 'uebertragen' | 'fehlerhaft';
  readonly versuche: number;
  readonly letzterfehler: string | null;
  readonly navbelegnummer: string | null;
  readonly uebertragenam: string | null;
  readonly aufgegebenam: string | null;
  readonly angelegtam: string;
  readonly naechsterversuch: string;
  readonly zuletzt: string;
  readonly kennzeichen: string | null;
  readonly bezeichnung: string | null;
};

export type Protokollfilter = {
  readonly belegart?: Protokollzeile['belegart'];
  readonly zustand?: Protokollzeile['zustand'];
  readonly nurAufgegebene?: boolean;
  readonly suche?: string;
  readonly grenze?: number;
  readonly ab?: number;
};

export type Abgleich = {
  readonly tag: string;
  readonly umgebung: Umgebungskennung;
  readonly crmanzahl: number;
  readonly crmsumme: string;
  readonly navanzahl: number | null;
  readonly navsumme: string | null;
  readonly abweichung: boolean;
  readonly gelaufenam: string;
};

export type Stand = {
  readonly umgebung: Umgebung | undefined;
  readonly erreichbar: boolean;
  readonly warteschlange: readonly Standzeile[];
  readonly aufgegeben: readonly Uebertragung[];
  readonly abgleiche: readonly Abgleich[];
};

export async function stand(): Promise<Stand> {
  return rufe('/navision/stand');
}

export async function umgebungen(): Promise<readonly Umgebung[]> {
  return rufe('/navision/umgebungen');
}

export async function umschalten(auf: Umgebungskennung): Promise<readonly Umgebung[]> {
  return rufe(`/navision/umgebungen/${auf}/aktiv`, { verfahren: 'PUT', rumpf: {} });
}

export async function endpunkteSetzen(
  umgebung: Umgebungskennung,
  eingabe: {
    readonly soapEndpunkt: string | null;
    readonly odataEndpunkt: string | null;
    readonly zugangsschluessel: string | null;
    readonly adapter: 'attrappe' | 'soap';
  },
): Promise<readonly Umgebung[]> {
  return rufe(`/navision/umgebungen/${umgebung}`, { verfahren: 'PUT', rumpf: eingabe });
}

export async function mapping(umgebung: Umgebungskennung): Promise<readonly Mappingzeile[]> {
  return rufe(`/navision/mapping?umgebung=${umgebung}`);
}

export async function mappingSetzen(eingabe: {
  readonly umgebung: Umgebungskennung;
  readonly art: Mappingart;
  readonly quelle: string;
  readonly ziel: string;
  readonly bemerkung: string | null;
}): Promise<readonly Mappingzeile[]> {
  return rufe('/navision/mapping', { verfahren: 'PUT', rumpf: eingabe });
}

export async function mappingEntfernen(
  umgebung: Umgebungskennung,
  art: Mappingart,
  quelle: string,
): Promise<readonly Mappingzeile[]> {
  return rufe(
    `/navision/mapping?umgebung=${umgebung}&art=${art}&quelle=${encodeURIComponent(quelle)}`,
    { verfahren: 'DELETE' },
  );
}

/** FIB-09: einen aufgegebenen Beleg zurückholen. */
/**
 * Den vorhandenen Stammdatenbestand einreihen (FIB-03, FIB-05, Kap. 29).
 *
 * `erneut` reiht auch das ein, was sich NICHT geändert hat — für den Fall,
 * dass die Gegenseite ihren Bestand verloren hat. Nur Stammdaten; Belege
 * fasst dieser Weg nicht an.
 */
export async function nachziehen(
  art: 'debitor' | 'artikel',
  erneut = false,
): Promise<{ readonly gesehen: number; readonly eingereiht: number }> {
  return rufe('/navision/nachziehen', { verfahren: 'POST', rumpf: { art, erneut } });
}

/**
 * Eine TESTUMGEBUNG zurücksetzen (FIB-18).
 *
 * Verwirft ihre Übertragungsgeschichte und reiht alles neu ein — Stammdaten
 * und jeden festgeschriebenen Beleg. Für den Fall, dass die Gegenseite einen
 * anderen Stand hat als unsere Aufzeichnung. Die produktive Umgebung weist
 * der Dienst ab (BP-15).
 */
export async function zuruecksetzen(kennung: Umgebungskennung): Promise<{
  readonly verworfen: number;
  readonly stammdaten: number;
  readonly belege: number;
}> {
  return rufe(`/navision/umgebungen/${kennung}/zuruecksetzen`, { verfahren: 'POST' });
}

/** FIB-11: die letzten Läufe. */
export async function protokoll(
  filter: Protokollfilter = {},
): Promise<{ readonly zeilen: readonly Protokollzeile[]; readonly gesamt: number }> {
  const teile = new URLSearchParams();
  for (const [name, wert] of Object.entries(filter)) {
    if (wert === undefined || wert === '' || wert === false) continue;
    teile.set(name, String(wert));
  }
  const angehaengt = teile.toString();
  return rufe(`/navision/protokoll${angehaengt === '' ? '' : `?${angehaengt}`}`);
}

/** LOG-11: Was ein Entfernen mit `tage` aus dem Protokoll nähme. */
export type Bereinigungsvorschau = {
  readonly frist: number;
  readonly untergrenze: number;
  readonly tage: number;
  readonly anzahl: number;
};

/** LOG-11: Ohne `tage` gilt die eingestellte Frist. */
export async function protokollBereinigung(tage?: number): Promise<Bereinigungsvorschau> {
  return rufe(`/navision/protokoll/bereinigung${tage === undefined ? '' : `?tage=${String(tage)}`}`);
}

/** LOG-11: abgeschlossene Einträge älter als `tage` aus dem Protokoll entfernen. */
export async function protokollBereinigen(
  tage: number,
): Promise<{ readonly anzahl: number; readonly tage: number }> {
  return rufe('/navision/protokoll/bereinigen', { verfahren: 'POST', rumpf: { tage } });
}

export async function wiederholen(id: string): Promise<{ readonly angestossen: true }> {
  return rufe(`/navision/uebertragung/${id}/wiederholen`, { verfahren: 'POST', rumpf: {} });
}

/** FIB-12: den Vollständigkeitsabgleich eines Tages anstoßen. */
export async function abgleichen(tag: string): Promise<Abgleich | undefined> {
  return rufe('/navision/abgleich', { verfahren: 'POST', rumpf: { tag } });
}
